<script lang="ts" setup>
import FlipnovelCenteredLoading from '@/components/FlipnovelCenteredLoading.vue'

const props = defineProps<{
  config: any
  skeleton: boolean
  isEmailLogin: number
  availableLogins: string[]
  showLogout: string | null
  form: any
  userAgreementUrl: string
  privacyPolicyUrl: string
  loginBgUrl: string
  signInWithFacebook: () => void
  signInWithGoogle: () => void
  signInWithApple: () => void
  logout: () => void
  setEmailLogin: () => void
  getCode: () => void
  beforeSignIn: () => void
  back: () => void
}>()

const emit = defineEmits<{
  (e: 'update-form-field', field: 'email' | 'code', value: string): void
}>()

const bgCovers = [
  { x: '-10%', y: '2%', r: '-14deg', s: 0.95 },
  { x: '24%', y: '6%', r: '11deg', s: 0.98 },
  { x: '62%', y: '3%', r: '-12deg', s: 0.96 },
  { x: '82%', y: '16%', r: '9deg', s: 0.92 },
  { x: '-8%', y: '24%', r: '13deg', s: 1.02 },
  { x: '17%', y: '28%', r: '-10deg', s: 0.96 },
  { x: '48%', y: '27%', r: '8deg', s: 0.94 },
  { x: '73%', y: '34%', r: '-15deg', s: 0.98 },
  { x: '4%', y: '46%', r: '-9deg', s: 1.01 },
  { x: '34%', y: '52%', r: '12deg', s: 0.95 },
  { x: '64%', y: '51%', r: '-11deg', s: 0.97 },
  { x: '85%', y: '58%', r: '10deg', s: 0.93 },
  { x: '-5%', y: '70%', r: '12deg', s: 0.98 },
  { x: '24%', y: '74%', r: '-8deg', s: 0.95 },
  { x: '56%', y: '76%', r: '10deg', s: 0.92 },
  { x: '82%', y: '80%', r: '-13deg', s: 1 },
]

function updateFormField(field: 'email' | 'code', event: Event) {
  emit('update-form-field', field, (event.target as HTMLInputElement).value)
}
</script>

<template>
  <div class="rr-login-page">
    <div v-if="!skeleton" class="login-bg">
      <div class="rr-book-covers" aria-hidden="true">
        <span
          v-for="(item, idx) in bgCovers"
          :key="idx"
          class="rr-book-covers__item"
          :style="{
            left: item.x,
            top: item.y,
            transform: `rotate(${item.r}) scale(${item.s})`,
          }"
        />
      </div>
      <div class="rr-login-overlay" />
      <div class="rr-login-content">
        <div class="fixed top-24px left-12px z-10" @click="back()">
          <i-icon-park-outline-left class="w-6 h-6 color-n-text-2" />
        </div>

        <template v-if="!isEmailLogin">
          <div class="rr-brand-wrap">
            <img :src="config.resources.logo" width="62" class="rr-brand-logo">
            <h1 class="rr-title">
              Welcome to {{ config.texts.brandTitle || config.texts.siteName || 'your next story' }}
            </h1>
            <p class="rr-subtitle">
              {{ config.texts.brandSubtitle || 'Romance, comfort, and feel-good stories in one place.' }}
            </p>
          </div>

          <div class="rr-main-actions">
            <button
              v-if="availableLogins.includes('facebook')"
              class="rr-main-btn rr-main-btn-facebook"
              type="button"
              @click.stop.prevent="signInWithFacebook"
            >
              <img :src="config.resources.facebookIcon" width="20" class="rr-main-btn__icon">
              <span class="rr-main-btn__text">{{ config.texts.signInWithFacebook }}</span>
            </button>
            <button
              v-if="availableLogins.includes('google')"
              class="rr-main-btn rr-main-btn-google"
              type="button"
              @click.stop.prevent="signInWithGoogle"
            >
              <img :src="config.resources.googleIcon" width="20" class="rr-main-btn__icon">
              <span class="rr-main-btn__text">{{ config.texts.signInWithGoogle }}</span>
            </button>
            <button
              v-if="availableLogins.includes('apple')"
              class="rr-main-btn rr-main-btn-apple"
              type="button"
              @click.stop.prevent="signInWithApple"
            >
              <img :src="config.resources.appleIcon" width="24" class="rr-main-btn__icon rr-main-btn__icon-apple">
              <span class="rr-main-btn__text">{{ config.texts.signInWithApple }}</span>
            </button>
            <button v-if="showLogout === '1'" type="button" class="rr-main-btn rr-main-btn-facebook" @click.stop.prevent="logout">
              <span class="rr-main-btn__text">{{ config.texts.logout }}</span>
            </button>
            <div class="rr-email-link" @click.stop="setEmailLogin">
              {{ config.texts.continueWithEmail }}
            </div>
          </div>
        </template>

        <template v-else>
          <div class="rr-brand-wrap rr-brand-wrap-email">
            <img :src="config.resources.logo" width="58" class="rr-brand-logo">
            <h1 class="rr-title">
              {{ config.texts.signInWithEmail }}
            </h1>
          </div>

          <div class="w-320px max-w-[calc(100vw-36px)] rr-form-card mt-12px">
            <div class="text-n-text-3 text-12px mt-10px mb-5px">
              {{ config.texts.yourEmail }}
            </div>
            <div class="flex items-center space-x-5 relative border border-1px rounded-12px overflow-hidden" :style="{ borderColor: config.theme.inputBorder }">
              <img class="w-4 h-4 absolute left-4 top-14px" :src="config.resources.emailIcon">
              <input
                :value="props.form.email"
                class="!ml0 flex-1 h-11 w-full pl-10 pr-4 border-0 !outline-0 !bg-transparent text-n-text-1"
                placeholder=""
                maxlength="50"
                @input="updateFormField('email', $event)"
              >
            </div>

            <div class="text-n-text-3 text-12px mt-14px mb-5px">
              {{ config.texts.verificationCode }}
            </div>
            <div class="flex items-center space-x-5 relative border border-1px rounded-12px overflow-hidden" :style="{ borderColor: config.theme.inputBorder }">
              <img class="w-4 h-4 absolute left-4 top-14px" :src="config.resources.emailCodeIcon">
              <input
                :value="props.form.code"
                class="!ml0 flex-1 h-11 w-full pl-10 pr-4 border-0 !outline-0 !bg-transparent text-n-text-1"
                placeholder=""
                maxlength="6"
                @input="updateFormField('code', $event)"
              >
              <div v-if="form.timeout > 0" class="text-n-text-3 text-align-left pr-3">
                {{ form.timeout }}s
              </div>
              <div
                v-else
                class="text-n-primary text-12px text-align-left pr-3 font-700"
                @click="getCode"
              >
                {{ config.texts.getVerificationCode }}
              </div>
            </div>

            <div class="w-full h-45px text-n-text-inverse btn-solid line-height-45px text-center border-rd-14px mt-22px" @click="beforeSignIn">
              {{ config.texts.signIn }}
            </div>
          </div>
        </template>

        <div class="rr-footer-agreement">
          {{ config.texts.userAgreement }} <a :href="userAgreementUrl">{{ config.texts.userAgreementLink }}</a> {{ config.texts.and }} <a :href="privacyPolicyUrl">{{ config.texts.privacyPolicyLink }}</a>
        </div>
      </div>
    </div>

    <FlipnovelCenteredLoading v-else />
  </div>
</template>

<style lang="scss" scoped>
.rr-login-page {
  min-height: 100vh;
  background: #14181f;
}

.login-bg {
  width: 100vw;
  min-height: 100vh;
  background-image: v-bind(loginBgUrl);
  background-size: cover;
  background-position: center;
  display: flex;
  justify-content: center;
  position: relative;
  overflow: hidden;

  a {
    text-decoration: underline;
  }
}

.rr-login-overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background:
    linear-gradient(180deg, rgba(20, 24, 31, 0.72) 0%, rgba(20, 24, 31, 0.84) 42%, rgba(20, 24, 31, 0.92) 100%),
    radial-gradient(70% 30% at 16% -8%, rgba(255, 138, 76, 0.22) 0%, rgba(255, 138, 76, 0) 70%),
    radial-gradient(72% 32% at 84% -12%, rgba(255, 107, 61, 0.18) 0%, rgba(255, 107, 61, 0) 72%);
}

.rr-book-covers {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}

.rr-book-covers__item {
  position: absolute;
  width: 120px;
  height: 160px;
  border-radius: 12px;
  background-image: v-bind(loginBgUrl);
  background-size: cover;
  background-position: center;
  opacity: 0.14;
  filter: grayscale(0.75) contrast(0.86) brightness(0.8);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.28);
}

.rr-login-content {
  position: relative;
  z-index: 2;
  width: 100%;
  padding: 84px 18px calc(28px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  align-items: center;
}

.rr-brand-wrap {
  margin-top: 14px;
  text-align: center;
}

.rr-brand-logo {
  display: block;
  margin: 0 auto;
}

.rr-brand-wrap-email {
  margin-top: 34px;
}

.rr-title {
  margin: 18px 0 0;
  color: #f8f7f5;
  font-size: 40px;
  line-height: 1.1;
  font-weight: 800;
}

.rr-subtitle {
  margin: 12px auto 0;
  max-width: 320px;
  color: rgba(248, 247, 245, 0.66);
  font-size: 16px;
  line-height: 1.4;
}

.rr-main-actions {
  width: 100%;
  max-width: 330px;
  margin-top: 28px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.rr-main-btn {
  height: 50px;
  width: 100%;
  border: 0;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0 18px 0 54px;
  box-sizing: border-box;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.2px;
  cursor: pointer;
  z-index: 3;
}

.rr-main-btn__icon {
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
}

.rr-main-btn__icon-apple {
  left: 18px;
}

.rr-main-btn__text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rr-main-btn-google {
  background: #474a54;
  color: #f8f7f5;
}

.rr-main-btn-facebook {
  background: #3478d7;
  color: #f8f7f5;
}

.rr-main-btn-apple {
  background: #f3f4f6;
  color: #14181f;
}

.rr-email-link {
  margin-top: 4px;
  height: 36px;
  color: #f8f7f5;
  font-size: 18px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  z-index: 2;
}

.rr-form-card {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.04);
}

.rr-footer-agreement {
  margin-top: auto;
  width: min(360px, calc(100vw - 32px));
  text-align: center;
  padding: 8px 0;
  color: rgba(248, 247, 245, 0.6);
  font-size: 12px;
  line-height: 1.5;
}
</style>
